import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  BadgeCheck,
  BarChart3,
  Check,
  ChevronRight,
  Code2,
  LockKeyhole,
  Menu,
  Radio,
  Send,
  ShieldCheck,
  Sparkles,
  WalletCards,
  X,
  Zap,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { PesaJetLogo } from '../components/PesaJetLogo';
import { NetworkBrand } from '../components/NetworkBrand';
import { SEOHead } from '../components/SEOHead';
import { getSEOConfig, generateWebPageSchema, generateServiceSchema } from '../utils/seo';
import { apiClient } from '../lib/api-client';

const features = [
  {
    icon: Send,
    number: '01',
    title: 'Move money, one API',
    text: 'Collect and disburse across MTN MoMo and Airtel Money with one predictable integration.',
  },
  {
    icon: Radio,
    number: '02',
    title: 'Know what is happening',
    text: 'Live transaction states, useful webhooks and a clean operations trail for every payment.',
  },
  {
    icon: ShieldCheck,
    number: '03',
    title: 'Built for real operations',
    text: 'KYC controls, signed requests, scoped API keys and practical tools for your finance team.',
  },
];

const activity = [
  {
    initials: 'AM',
    label: 'Airtel collection',
    detail: '0772 ••• 419',
    amount: '+ UGX 850,000',
    tone: 'red',
  },
  {
    initials: 'MT',
    label: 'Merchant payout',
    detail: '0783 ••• 082',
    amount: '− UGX 320,000',
    tone: 'yellow',
  },
  {
    initials: 'AM',
    label: 'Airtel collection',
    detail: '0701 ••• 638',
    amount: '+ UGX 124,500',
    tone: 'red',
  },
];

export function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [codeLang, setCodeLang] = useState<'rest' | 'node' | 'python' | 'php'>('node');
  const [transactionFeePercentage, setTransactionFeePercentage] = useState(3.5);
  const seoConfig = getSEOConfig('home');
  const structuredData = [generateWebPageSchema('home'), generateServiceSchema()];

  useEffect(() => {
    let active = true;

    apiClient
      .get<{ data?: { transactionFeePercentage?: number } }>('/providers/pricing')
      .then(({ data }) => {
        const percentage = Number(data.data?.transactionFeePercentage);
        if (active && Number.isFinite(percentage) && percentage >= 0) {
          setTransactionFeePercentage(percentage);
        }
      })
      .catch(() => {
        // Keep the public pricing fallback when the API is unavailable.
      });

    return () => {
      active = false;
    };
  }, []);

  return (
    <>
      <SEOHead {...seoConfig} structuredData={structuredData} />
      <div className="marketing-site">
        <header className="marketing-nav">
          <Link to="/" className="brand-link" aria-label="PesaJet Pay home">
            <PesaJetLogo size="md" variant="full" />
          </Link>
          <nav className="marketing-links" aria-label="Primary navigation">
            <a href="#product">Product</a>
            <a href="#developers">Developers</a>
            <a href="#pricing">Pricing</a>
          </nav>
          <div className="marketing-actions">
            <Link to="/login" className="text-action">
              Sign in
            </Link>
            <Link to="/signup" className="solid-action">
              Start building <ArrowUpRight size={16} />
            </Link>
          </div>
          <button
            className="menu-trigger"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-expanded={menuOpen}
            aria-label="Toggle menu"
          >
            {menuOpen ? <X /> : <Menu />}
          </button>
          {menuOpen && (
            <div className="mobile-menu">
              <a href="#product" onClick={() => setMenuOpen(false)}>
                Product
              </a>
              <a href="#developers" onClick={() => setMenuOpen(false)}>
                Developers
              </a>
              <a href="#pricing" onClick={() => setMenuOpen(false)}>
                Pricing
              </a>
              <Link to="/login">Sign in</Link>
              <Link to="/signup" className="solid-action">
                Start building <ArrowRight size={16} />
              </Link>
            </div>
          )}
        </header>

        <main>
          <section className="hero-section">
            <div className="hero-copy">
              <div className="eyebrow">
                <span /> Payment infrastructure for East Africa
              </div>
              <h1>
                Money moves.
                <br />
                <em>Your business</em>
                <br />
                moves faster.
              </h1>
              <p>
                Accept mobile money, automate payouts and reconcile every shilling from one
                beautifully simple platform.
              </p>
              <div className="hero-actions">
                <Link to="/signup" className="solid-action solid-action-lg">
                  Create an account <ArrowRight size={18} />
                </Link>
                <a href="#developers" className="quiet-action">
                  <Code2 size={18} /> Read the API docs
                </a>
              </div>
              <div className="trust-line">
                <span>
                  <Check size={14} /> No setup fee
                </span>
                <span>
                  <Check size={14} /> Go live quickly
                </span>
                <span>
                  <Check size={14} /> Local support
                </span>
              </div>
            </div>

            <div className="hero-visual" aria-label="PesaJet dashboard preview">
              <div className="orbit orbit-one" />
              <div className="orbit orbit-two" />
              <div className="dashboard-preview">
                <div className="preview-topbar">
                  <div className="preview-mark">
                    <Zap size={16} fill="currentColor" />
                  </div>
                  <div className="preview-title">
                    <b>Overview</b>
                    <span>Live</span>
                  </div>
                  <div className="preview-avatar">MA</div>
                </div>
                <div className="balance-panel">
                  <span>Available balance</span>
                  <strong>UGX 24,680,400</strong>
                  <div>
                    <small>+12.8% this month</small>
                    <button>
                      <ArrowUpRight size={15} /> Send money
                    </button>
                  </div>
                </div>
                <div className="preview-stats">
                  <div>
                    <span>Today</span>
                    <strong>UGX 3.8M</strong>
                    <small>142 payments</small>
                  </div>
                  <div>
                    <span>Success rate</span>
                    <strong>98.7%</strong>
                    <small className="positive">Healthy</small>
                  </div>
                </div>
                <div className="activity-panel">
                  <div className="activity-heading">
                    <b>Recent activity</b>
                    <span>
                      View all <ChevronRight size={13} />
                    </span>
                  </div>
                  {activity.map((item) => (
                    <div className="activity-row" key={`${item.detail}-${item.amount}`}>
                      <span className={`provider-dot ${item.tone}`}>{item.initials}</span>
                      <div>
                        <b>{item.label}</b>
                        <small>{item.detail}</small>
                      </div>
                      <strong>{item.amount}</strong>
                    </div>
                  ))}
                </div>
              </div>
              <div className="floating-status">
                <BadgeCheck size={22} />
                <div>
                  <b>Payment complete</b>
                  <span>UGX 850,000 received</span>
                </div>
              </div>
              <div className="floating-code">
                <span>POST</span>
                <code>/v1/collections</code>
                <Check size={15} />
              </div>
            </div>
          </section>

          <section className="provider-strip" aria-label="Supported providers">
            <p>One integration. The networks your customers already use.</p>
            <div>
              <NetworkBrand provider="mtn" />
              <i />
              <NetworkBrand provider="airtel" />
              <i />
              <span className="more-networks">More networks coming</span>
            </div>
          </section>

          <section className="product-section" id="product">
            <div className="section-intro">
              <span className="section-kicker">A better payment layer</span>
              <h2>
                Complex rails.
                <br />
                Refreshingly simple tools.
              </h2>
              <p>
                We handle provider differences behind the scenes, so your team can focus on products
                and customers.
              </p>
            </div>
            <div className="feature-grid">
              {features.map(({ icon: Icon, number, title, text }) => (
                <article className="feature-card" key={title}>
                  <div className="feature-meta">
                    <span>{number}</span>
                    <Icon size={22} />
                  </div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                  <a href="#developers">
                    Explore feature <ArrowUpRight size={15} />
                  </a>
                </article>
              ))}
            </div>
          </section>

          <section className="developer-section" id="developers">
            <div className="developer-copy">
              <span className="section-kicker light">Made for builders</span>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', margin: '8px 0 16px' }}>
                <a
                  href="https://www.npmjs.com/package/@pesajet/sdk"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 14px',
                    background: 'rgba(255, 255, 255, 0.08)',
                    border: '1px solid rgba(255, 255, 255, 0.16)',
                    borderRadius: '100px',
                    color: '#e2e8f0',
                    fontSize: '12px',
                    fontWeight: 700,
                    textDecoration: 'none',
                    width: 'fit-content',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <span
                    style={{
                      background: '#cb3837',
                      color: 'white',
                      fontSize: '10px',
                      fontWeight: 800,
                      padding: '2px 6px',
                      borderRadius: '4px',
                      letterSpacing: '0.05em',
                    }}
                  >
                    NPM
                  </span>
                  <span>@pesajet/sdk</span>
                  <span style={{ color: '#94a3b8', fontSize: '11px' }}>v1.0.0</span>
                  <ArrowUpRight size={13} style={{ color: '#94a3b8' }} />
                </a>
                <a
                  href="https://pypi.org/project/pesajet/1.0.0/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 14px',
                    background: 'rgba(255, 255, 255, 0.08)',
                    border: '1px solid rgba(255, 255, 255, 0.16)',
                    borderRadius: '100px',
                    color: '#e2e8f0',
                    fontSize: '12px',
                    fontWeight: 700,
                    textDecoration: 'none',
                    width: 'fit-content',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <span
                    style={{
                      background: '#3775a9',
                      color: 'white',
                      fontSize: '10px',
                      fontWeight: 800,
                      padding: '2px 6px',
                      borderRadius: '4px',
                      letterSpacing: '0.05em',
                    }}
                  >
                    PYPI
                  </span>
                  <span>pesajet</span>
                  <span style={{ color: '#94a3b8', fontSize: '11px' }}>v1.0.0</span>
                  <ArrowUpRight size={13} style={{ color: '#94a3b8' }} />
                </a>
              </div>
              <h2>
                From first REST call
                <br />
                to first payment.
              </h2>
              <p>
                A unified, predictable REST API for any programming language or backend, plus
                official SDKs like <code>@pesajet/sdk</code> on npm and <code>pesajet</code> on
                PyPI, and instant webhooks.
              </p>
              <ul>
                <li>
                  <LockKeyhole size={18} /> Standard REST / JSON payloads
                </li>
                <li>
                  <Zap size={18} /> Fast sandbox & zero setup friction
                </li>
                <li>
                  <BarChart3 size={18} /> Observable status & instant webhooks
                </li>
              </ul>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                <Link to="/signup" className="lime-action">
                  Get sandbox access <ArrowRight size={17} />
                </Link>
                <Link
                  to="/docs"
                  className="quiet-action"
                  style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.2)' }}
                >
                  <Code2 size={16} /> Explore API docs
                </Link>
              </div>
            </div>
            <div className="code-window">
              <div className="code-tabs">
                <button
                  type="button"
                  className={codeLang === 'rest' ? 'active' : ''}
                  onClick={() => setCodeLang('rest')}
                  style={{
                    background: 'transparent',
                    border: 0,
                    cursor: 'pointer',
                    font: 'inherit',
                  }}
                >
                  REST API (cURL)
                </button>
                <button
                  type="button"
                  className={codeLang === 'node' ? 'active' : ''}
                  onClick={() => setCodeLang('node')}
                  style={{
                    background: 'transparent',
                    border: 0,
                    cursor: 'pointer',
                    font: 'inherit',
                  }}
                >
                  Node.js (@pesajet/sdk)
                </button>
                <button
                  type="button"
                  className={codeLang === 'python' ? 'active' : ''}
                  onClick={() => setCodeLang('python')}
                  style={{
                    background: 'transparent',
                    border: 0,
                    cursor: 'pointer',
                    font: 'inherit',
                  }}
                >
                  Python (pesajet)
                </button>
                <button
                  type="button"
                  className={codeLang === 'php' ? 'active' : ''}
                  onClick={() => setCodeLang('php')}
                  style={{
                    background: 'transparent',
                    border: 0,
                    cursor: 'pointer',
                    font: 'inherit',
                  }}
                >
                  PHP
                </button>
                <i />
              </div>
              <pre>
                {codeLang === 'rest' && (
                  <code>
                    <span className="code-muted"># Direct REST API POST /payments</span>
                    {`\n`}
                    <span className="code-purple">curl</span> -X POST
                    https://payments.pesajet.com/api/v1/payments \{`\n`}
                    {'  '}-H <span className="code-orange">"Content-Type: application/json"</span> \
                    {`\n`}
                    {'  '}-H <span className="code-orange">"X-API-Key: pk_live_..."</span> \{`\n`}
                    {'  '}-d <span className="code-lime">'{'{'}</span>
                    {`\n`}
                    {'    '}
                    <span className="code-orange">"type"</span>:{' '}
                    <span className="code-orange">"COLLECTION"</span>,{`\n`}
                    {'    '}
                    <span className="code-orange">"amount"</span>:{' '}
                    <span className="code-lime">850000</span>,{`\n`}
                    {'    '}
                    <span className="code-orange">"currency"</span>:{' '}
                    <span className="code-orange">"UGX"</span>,{`\n`}
                    {'    '}
                    <span className="code-orange">"phoneNumber"</span>:{' '}
                    <span className="code-orange">"+256772••••19"</span>,{`\n`}
                    {'    '}
                    <span className="code-orange">"provider"</span>:{' '}
                    <span className="code-orange">"mtn"</span>,{`\n`}
                    {'    '}
                    <span className="code-orange">"reference"</span>:{' '}
                    <span className="code-orange">"INV-8821"</span>
                    {`\n`}
                    {'  '}
                    <span className="code-lime">{'}'}</span>'{`\n\n`}
                    <span className="code-muted">
                      // Status: 200 OK | payment.status: 'PENDING'
                    </span>
                  </code>
                )}
                {codeLang === 'node' && (
                  <code>
                    <span className="code-muted">// npm install @pesajet/sdk</span>
                    {`\n`}
                    <span className="code-purple">import</span> {'{'}{' '}
                    <span className="code-orange">PesaJet</span> {'}'}{' '}
                    <span className="code-purple">from</span>{' '}
                    <span className="code-orange">'@pesajet/sdk'</span>;{`\n\n`}
                    <span className="code-purple">const</span> pesajet ={' '}
                    <span className="code-purple">new</span>{' '}
                    <span className="code-orange">PesaJet</span>({'{'} apiKey:{' '}
                    <span className="code-orange">'pk_live_...'</span> {'}'});{`\n\n`}
                    <span className="code-purple">const</span> payment ={' '}
                    <span className="code-purple">await</span> pesajet.payments.create({'{'}
                    {`\n`} amount: <span className="code-lime">850000</span>,{`\n`} currency:{' '}
                    <span className="code-orange">'UGX'</span>,{`\n`} phoneNumber:{' '}
                    <span className="code-orange">'+256772••••19'</span>,{`\n`} provider:{' '}
                    <span className="code-orange">'mtn'</span>,{`\n`} reference:{' '}
                    <span className="code-orange">'INV-8821'</span>
                    {`\n`}
                    {'}'});{`\n\n`}
                    <span className="code-muted">// payment.status =&gt; 'PENDING'</span>
                  </code>
                )}
                {codeLang === 'python' && (
                  <code>
                    <span className="code-muted"># pip install pesajet</span>
                    {`\n`}
                    <span className="code-purple">from</span> pesajet{' '}
                    <span className="code-purple">import</span>{' '}
                    <span className="code-orange">PesaJet</span>
                    {`\n\n`}
                    pesajet = <span className="code-orange">PesaJet</span>(api_key=
                    <span className="code-orange">"pk_live_..."</span>){`\n\n`}
                    payment = pesajet.payments.create({`\n`} amount=
                    <span className="code-lime">850000</span>,{`\n`} currency=
                    <span className="code-orange">"UGX"</span>,{`\n`} phone_number=
                    <span className="code-orange">"+256772••••19"</span>,{`\n`} provider=
                    <span className="code-orange">"mtn"</span>,{`\n`} reference=
                    <span className="code-orange">"INV-8821"</span>
                    {`\n`}){`\n\n`}
                    <span className="code-muted"># payment['status'] =&gt; 'PENDING'</span>
                  </code>
                )}
                {codeLang === 'php' && (
                  <code>
                    <span className="code-purple">use</span>{' '}
                    <span className="code-orange">PesaJet\PesaJet</span>;{`\n\n`}
                    $pesajet = <span className="code-purple">new</span>{' '}
                    <span className="code-orange">PesaJet</span>([
                    <span className="code-orange">'apiKey'</span> =&gt;{' '}
                    <span className="code-orange">'pk_live_...'</span>]);{`\n\n`}
                    $payment = $pesajet-&gt;payments-&gt;create([{`\n`}{' '}
                    <span className="code-orange">'amount'</span> =&gt;{' '}
                    <span className="code-lime">850000</span>,{`\n`}{' '}
                    <span className="code-orange">'phoneNumber'</span> =&gt;{' '}
                    <span className="code-orange">'+256772••••19'</span>,{`\n`}{' '}
                    <span className="code-orange">'provider'</span> =&gt;{' '}
                    <span className="code-orange">'mtn'</span>,{`\n`}{' '}
                    <span className="code-orange">'reference'</span> =&gt;{' '}
                    <span className="code-orange">'INV-8821'</span>
                    {`\n`}]);
                  </code>
                )}
              </pre>
              <div className="code-response">
                <span>
                  <Sparkles size={15} /> Response in 284ms
                </span>
                <b>200 OK</b>
              </div>
            </div>
          </section>

          <section className="pricing-section" id="pricing">
            <div>
              <span className="section-kicker">Simple by design</span>
              <h2>
                Pricing you can
                <br />
                explain in one line.
              </h2>
            </div>
            <div className="price-card">
              <div>
                <strong>{transactionFeePercentage}%</strong>
                <span>per successful transaction</span>
              </div>
              <ul>
                <li>
                  <Check /> No setup costs
                </li>
                <li>
                  <Check /> Unlimited API requests
                </li>
                <li>
                  <Check /> Reconciliation included
                </li>
                <li>
                  <Check /> Human support included
                </li>
              </ul>
              <Link to="/signup" className="solid-action solid-action-lg">
                Start building <ArrowRight size={18} />
              </Link>
            </div>
          </section>

          <section className="final-cta">
            <WalletCards size={36} />
            <h2>
              Ready to move money
              <br />
              without the friction?
            </h2>
            <p>Create your PesaJet account and make your first sandbox payment today.</p>
            <Link to="/signup" className="lime-action">
              Create free account <ArrowRight size={18} />
            </Link>
          </section>
        </main>

        <footer className="marketing-footer">
          <PesaJetLogo size="md" variant="full" />
          <p>Payment infrastructure, built in Uganda.</p>
          <div>
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
            <a
              href="https://www.npmjs.com/package/@pesajet/sdk"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
            >
              Node SDK (npm) <ArrowUpRight size={12} />
            </a>
            <a
              href="https://pypi.org/project/pesajet/1.0.0/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
            >
              Python SDK (PyPI) <ArrowUpRight size={12} />
            </a>
            <a href="mailto:support@pesajet.com">Support</a>
            <a
              href="https://chat.whatsapp.com/FDEpAAdwm5kAsd1KcgHSsg?s=cl&p=i&mlu=0&ilr=0&amv=2"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5"
            >
              <img src="/whatsapp.svg" alt="WhatsApp" className="w-4 h-4" /> WhatsApp
            </a>
            <span>© {new Date().getFullYear()} PesaJet</span>
          </div>
        </footer>
      </div>
    </>
  );
}
